<script setup>
import { ref, watch, nextTick, onMounted } from 'vue';

const props = defineProps({
  modelValue: {
    type: String,
    default: '',
  },
  length: {
    type: Number,
    default: 6,
  },
  autofocus: {
    type: Boolean,
    default: true,
  },
  disabled: {
    type: Boolean,
    default: false,
  },
  error: {
    type: Boolean,
    default: false,
  },
  // Visual variant: "light" (dark bg) or "default" (adapts to theme)
  variant: {
    type: String,
    default: 'default',
    validator: v => ['default', 'light'].includes(v),
  },
});

const emit = defineEmits(['update:modelValue', 'complete']);

// Each box holds a single digit
const digits = ref(Array(props.length).fill(''));
const inputRefs = ref([]);

function syncFromValue(value) {
  const clean = (value || '').replace(/\D/g, '').slice(0, props.length);
  for (let i = 0; i < props.length; i += 1) {
    digits.value[i] = clean[i] || '';
  }
}

function emitValue() {
  const joined = digits.value.join('');
  emit('update:modelValue', joined);
  if (joined.length === props.length && !digits.value.includes('')) {
    emit('complete', joined);
  }
}

function focusIndex(i) {
  nextTick(() => {
    const el = inputRefs.value[i];
    if (el) {
      el.focus();
      // Select whatever is in the box so typing replaces it
      el.select?.();
    }
  });
}

function onInput(event, index) {
  const raw = event.target.value;
  // Keep only the last typed digit
  const digit = raw.replace(/\D/g, '').slice(-1);
  digits.value[index] = digit;
  emitValue();

  if (digit && index < props.length - 1) {
    focusIndex(index + 1);
  }
}

function onKeydown(event, index) {
  const key = event.key;

  if (key === 'Backspace') {
    if (digits.value[index]) {
      digits.value[index] = '';
      emitValue();
      event.preventDefault();
      return;
    }
    // Empty box: hop back and clear the previous one
    if (index > 0) {
      event.preventDefault();
      digits.value[index - 1] = '';
      emitValue();
      focusIndex(index - 1);
    }
    return;
  }

  if (key === 'ArrowLeft' && index > 0) {
    event.preventDefault();
    focusIndex(index - 1);
    return;
  }

  if (key === 'ArrowRight' && index < props.length - 1) {
    event.preventDefault();
    focusIndex(index + 1);
    return;
  }

  if (key === 'Enter') {
    if (digits.value.join('').length === props.length) {
      emit('complete', digits.value.join(''));
    }
  }
}

function onPaste(event) {
  event.preventDefault();
  const text =
    (event.clipboardData || window.clipboardData)?.getData('text') || '';
  const clean = text.replace(/\D/g, '').slice(0, props.length);
  if (!clean) return;

  for (let i = 0; i < props.length; i += 1) {
    digits.value[i] = clean[i] || '';
  }
  emitValue();
  // Focus the first empty box after paste, or the last if full
  const firstEmpty = digits.value.findIndex(d => !d);
  focusIndex(firstEmpty === -1 ? props.length - 1 : firstEmpty);
}

function onFocus(event) {
  // Select current digit so typing replaces it
  event.target.select?.();
}

// Sync external v-model changes (e.g. parent reset)
watch(
  () => props.modelValue,
  newVal => {
    const joined = digits.value.join('');
    if (newVal !== joined) {
      syncFromValue(newVal);
    }
  }
);

onMounted(() => {
  syncFromValue(props.modelValue);
  if (props.autofocus) focusIndex(0);
});
</script>

<template>
  <div class="flex items-center justify-center gap-2 sm:gap-3" @paste="onPaste">
    <input
      v-for="(_, index) in digits"
      :key="index"
      :ref="el => (inputRefs[index] = el)"
      :value="digits[index]"
      type="text"
      inputmode="numeric"
      pattern="[0-9]*"
      maxlength="1"
      :autocomplete="index === 0 ? 'one-time-code' : 'off'"
      :disabled="disabled"
      class="otp-box"
      :class="[
        variant === 'light' ? 'otp-box--light' : 'otp-box--default',
        { 'otp-box--error': error, 'otp-box--filled': !!digits[index] },
      ]"
      @input="onInput($event, index)"
      @keydown="onKeydown($event, index)"
      @focus="onFocus"
    />
  </div>
</template>

<style scoped>
.otp-box {
  width: 3rem;
  height: 3.5rem;
  text-align: center;
  font-size: 1.5rem;
  font-weight: 600;
  font-variant-numeric: tabular-nums;
  border: 2px solid;
  border-radius: 0.75rem;
  outline: none;
  transition:
    border-color 0.15s ease,
    box-shadow 0.15s ease,
    transform 0.05s ease;
  caret-color: transparent;
}

@media (max-width: 380px) {
  .otp-box {
    width: 2.5rem;
    height: 3rem;
    font-size: 1.25rem;
  }
}

.otp-box:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

/* Default variant: adapts to dashboard theme */
.otp-box--default {
  background: rgb(var(--color-n-alpha-1, 247 248 250) / 1);
  border-color: rgb(var(--color-n-strong, 226 229 235) / 1);
  color: rgb(var(--color-n-slate-12, 17 24 39) / 1);
}

.otp-box--default:focus {
  border-color: rgb(var(--color-n-brand, 99 102 241) / 1);
  box-shadow: 0 0 0 3px rgb(var(--color-n-brand, 99 102 241) / 0.2);
}

.otp-box--default.otp-box--filled {
  background: rgb(var(--color-n-solid-2, 255 255 255) / 1);
  border-color: rgb(var(--color-n-brand, 99 102 241) / 0.5);
}

/* "Light" variant: pensado para el login gubernamental. Por defecto usa la
 * paleta clara (fondo blanco); los overrides para `html.dark` viven en el
 * bloque de estilos sin `scoped` más abajo para que `html.dark` sí haga match
 * con los inputs (Vue scoped hashea la clase del componente pero respeta los
 * selectores ancestros). */
.otp-box--light {
  background: #f8fafc;
  border-color: #cbd5e1;
  color: #1c1e4d;
}

.otp-box--light:focus {
  border-color: #1c1e4d;
  background: #ffffff;
  box-shadow: 0 0 0 3px rgba(28, 30, 77, 0.18);
}

.otp-box--light.otp-box--filled {
  background: #ffffff;
  border-color: rgba(28, 30, 77, 0.5);
}

/* Error state */
.otp-box--error {
  border-color: rgb(220 38 38) !important;
  animation: shake 0.3s ease-in-out;
}

@keyframes shake {
  0%,
  100% {
    transform: translateX(0);
  }
  25% {
    transform: translateX(-3px);
  }
  75% {
    transform: translateX(3px);
  }
}
</style>

<style>
/* Overrides de dark mode para el variant "light". Fuera de `scoped` porque
 * necesitamos que `html.dark` como selector ancestro haga match. */
html.dark .otp-box--light {
  background: rgba(255, 255, 255, 0.06);
  border-color: rgba(255, 255, 255, 0.15);
  color: white;
}

html.dark .otp-box--light:focus {
  border-color: rgb(212 175 55);
  background: rgba(255, 255, 255, 0.1);
  box-shadow: 0 0 0 3px rgba(212, 175, 55, 0.25);
}

html.dark .otp-box--light.otp-box--filled {
  background: rgba(212, 175, 55, 0.08);
  border-color: rgba(212, 175, 55, 0.5);
}
</style>
